// ADR-0153 (fase 3, corte 1, #0750) — las encuestas del propietario.
//
// Propietario (mismo gate que /me/notifications: 403
// NOTIFICATIONS_ACTOR_NOT_ALLOWED para otro rol):
//   GET  /me/surveys/:id          → la encuesta: pregunta, escala, respuesta
//   POST /me/surveys/:id/score    → { score } — registra el número (una vez)
//   POST /me/surveys/:id/comment  → { comment } — el comentario opcional
//                                   (una vez, sólo después del número)
//
// Gerente, jefe y admin (ver `postventa-dashboard.ts`):
//   GET /postventa-dashboard/survey-summary?assetGroupId=  (los agregados y
//   las 200 respuestas más recientes, con el total)
//
// El puntaje y el comentario se mandan en dos POST para que el número
// quede registrado aunque el propietario no escriba nada después. Un GET
// nunca registra nada: el `?score=n` de los enlaces del correo sólo
// preselecciona en la pantalla (los escáneres de correo abren los
// enlaces).
//
// El contratista nunca ve una nota: nada de esto viaja por el carril
// `share_link` (`/public/b/:token`).
import { OWNER_SURVEY_COMMENT_MAX, ownerSurveyKindSchema, ownerSurveyScale, } from "@bitacorapro/core-engine";
import { z } from "zod";
/** 404 — la encuesta no existe, no es de su org o no es suya. */
export const SURVEY_NOT_FOUND = "SURVEY_NOT_FOUND";
/** 409 — ya no es dueño de la unidad: la encuesta ya no es suya. */
export const SURVEY_RECIPIENT_NOT_OWNER = "SURVEY_RECIPIENT_NOT_OWNER";
/** 409 — ya respondió (el número, o el comentario). */
export const SURVEY_ALREADY_ANSWERED = "SURVEY_ALREADY_ANSWERED";
/** 409 — el comentario va después del número. */
export const SURVEY_COMMENT_REQUIRES_SCORE = "SURVEY_COMMENT_REQUIRES_SCORE";
/** 422 — el número está fuera de la escala de esa encuesta. */
export const SURVEY_SCORE_OUT_OF_RANGE = "SURVEY_SCORE_OUT_OF_RANGE";
/** Las preguntas, del prototipo de Mario. */
export const OWNER_SURVEY_QUESTION = {
    nps_2m: "¿Qué tan probable es que nos recomiendes?",
    nps_9m: "¿Qué tan probable es que nos recomiendes?",
    visita: "¿Cómo fue la visita técnica?",
};
/** Las etiquetas de los extremos de cada escala. Las del NPS son de
 *  Mario; las de la visita (1–5), provisionales hasta que las valide
 *  (ADR-0153, pendiente de revisión). */
export const OWNER_SURVEY_SCALE_LABELS = {
    nps: { minLabel: "Nada probable", maxLabel: "Muy probable" },
    visita: { minLabel: "Muy mala", maxLabel: "Muy buena" },
};
/** «Responder es opcional»: parte de la base de licitud (ADR-0153). */
export const OWNER_SURVEY_OPTIONAL_NOTE = "Responder es opcional.";
export const ownerSurveyScaleSchema = z.object({
    min: z.number().int(),
    max: z.number().int(),
    minLabel: z.string(),
    maxLabel: z.string(),
});
/** La escala de una encuesta, con sus etiquetas. */
export function ownerSurveyScaleFor(kind) {
    const { min, max } = ownerSurveyScale(kind);
    const labels = kind === "visita" ? OWNER_SURVEY_SCALE_LABELS.visita : OWNER_SURVEY_SCALE_LABELS.nps;
    return { min, max, ...labels };
}
/**
 * El cuerpo de la encuesta (la notificación y la pantalla). El de los 2
 * meses es de Mario; el de los 9 meses y el de la visita sin fecha son
 * provisionales (ADR-0153). `visitDay` llega ya formateado («18-ago»).
 */
export function ownerSurveyBody(kind, ctx = {}) {
    if (kind === "nps_2m")
        return "Cuéntanos cómo fue tu experiencia con la entrega de tu casa.";
    if (kind === "nps_9m")
        return "Cuéntanos cómo ha sido tu experiencia en estos meses.";
    // El rótulo de la observación va entre comillas, tal cual (es un título:
    // «Puerta de la cocina no cierra»), como «Sobre «…»» en las consultas.
    const label = ctx.observationLabel?.trim();
    const what = label ? `«${label}»` : "tu observación";
    return ctx.visitDay
        ? `Evalúa la visita del ${ctx.visitDay} por ${what}. Te toma un minuto.`
        : `Evalúa el arreglo de ${what}. Te toma un minuto.`;
}
/** «Gracias. Registramos tu respuesta: {n}» (Mario). */
export function ownerSurveyThanks(score) {
    return `Gracias. Registramos tu respuesta: ${score}`;
}
export const ownerSurveyResponseSchema = z.object({
    score: z.number().int(),
    comment: z.string().nullable(),
    respondedAt: z.string(),
});
export const ownerSurveyDetailResponseSchema = z.object({
    id: z.string().uuid(),
    kind: ownerSurveyKindSchema,
    title: z.string(),
    body: z.string(),
    scale: ownerSurveyScaleSchema,
    response: ownerSurveyResponseSchema.nullable(),
    assetId: z.string().uuid(),
    /** «{Proyecto} · Unidad {X}». */
    propiedadLabel: z.string().nullable(),
    /** El largo máximo del comentario. */
    commentMax: z.number().int().positive(),
});
export const ownerSurveyScoreBodySchema = z
    .object({
    // El rango se valida contra el tipo en el servidor (0–10 o 1–5).
    score: z.number().int(),
})
    .strict();
export const ownerSurveyCommentBodySchema = z
    .object({
    comment: z.string().trim().min(1).max(OWNER_SURVEY_COMMENT_MAX),
})
    .strict();
//# sourceMappingURL=owner-surveys.js.map